import Flickity from 'flickity';

import resolution from '../util/resolution';

import {makeGridSwatches} from './swatch';
import {Siblings} from './siblings';
import {CustomScrollbar} from './custom-scrollbar';

const selectors = {
  carousel: '[data-carousel]',
  carouselWithProgress: 'data-carousel-progress',
  carouselSlide: '[data-carousel-slide]',
  carouselFirstSlidePhoto: '[data-grid-slide]',
  pgiFirstSlidePhoto: 'product-grid-item-variant:not([hidden]) [data-grid-slide]',
  wrapper: '[data-wrapper]',
  carouselTrack: '[data-carousel-track]',
  slider: '.flickity-slider',
  carouselOptions: 'data-options',
  carouselCustomScrollbar: 'data-custom-scrollbar-items',
  carouselPrev: '.flickity-button.previous',
  carouselNext: '.flickity-button.next',
  recentlyViewHolder: 'data-recently-viewed-products',
  relatedHolder: 'data-related-products',
  sectionHolder: '[data-section-id]',
};

const classes = {
  wrapper: 'wrapper',
  arrowsForceTop: 'flickity-force-arrows-top',
  arrowsOnSide: 'not-moved-arrows',
  hide: 'hide',
  flickityEnabled: 'flickity-enabled',
  hiddenArrows: 'hidden-arrows',
  flickityStatic: 'flickity-static',
};

const offsets = {
  additionalOffsetWrapper: 112,
};

class Carousel extends HTMLElement {
  constructor() {
    super();

    this.container = this;
  }

  connectedCallback() {
    this.carousel = this.container.querySelector(selectors.carousel);
    this.carouselTrack = this.container.querySelector(selectors.carouselTrack);
    this.wrapper = this.container.closest(selectors.wrapper);
    this.section = this.container.closest(selectors.sectionHolder);
    this.slidesVisible = null;

    this.carouselInstance = null;
    this.carouselPrev = null;
    this.carouselNext = null;
    this.customOptions = {};

    this.toggleWrapperModifierEvent = () => this.toggleWrapperModifier();

    if (this.carousel && this.carousel.hasAttribute(selectors.recentlyViewHolder)) {
      // Check carousel in recently viewed products
      this.section.addEventListener('theme:recent-products:added', () => {
        this.init();
      });
    } else if (this.carousel && this.carousel.hasAttribute(selectors.relatedHolder)) {
      // Check carousel in recommendation products but without overwrite option
      this.section.addEventListener('theme:related-products:added', () => {
        this.init();
      });
    } else {
      this.init();
    }
  }

  init() {
    if (!this.carousel) {
      return;
    }

    this.slidesTotal = this.carousel.querySelectorAll(selectors.carouselSlide).length;

    this.getGridLayout();
    this.trackVisibleSlides();

    if (this.carousel.hasAttribute(selectors.carouselOptions)) {
      this.customOptions = JSON.parse(decodeURIComponent(this.carousel.getAttribute(selectors.carouselOptions)));
    }

    this.initCarousel();
    this.calculatedArrowsTopPosition();

    this.toggleWrapperModifier();
    document.addEventListener('theme:resize:width', this.toggleWrapperModifierEvent);

    if (this.carousel.hasAttribute(selectors.carouselWithProgress)) {
      this.progressBarCalculate();
    }

    if (this.carousel.hasAttribute(selectors.carouselCustomScrollbar)) {
      new CustomScrollbar(this.container);
    }
  }

  initCarousel() {
    this.options = {
      accessibility: true,
      contain: true,
      freeScroll: true,
      prevNextButtons: true,
      wrapArround: false,
      groupCells: false,
      autoPlay: false,
      pageDots: false,
      cellAlign: window.isRTL ? 'right' : 'left',
      rightToLeft: window.isRTL,
      dragThreshold: 10,
      arrowShape: {
        x0: 10,
        x1: 60,
        y1: 50,
        x2: 67.5,
        y2: 42.5,
        x3: 25,
      },
      on: {
        resize: () => {
          this.toggleArrows();
          this.calculatedArrowsTopPosition();

          setTimeout(() => {
            this.visibleSlides();
          }, 100);
        },
      },
      ...this.customOptions,
    };
    this.carouselInstance = new Flickity(this.carousel, this.options);
    this.carouselPrev = this.carousel.querySelector(selectors.carouselPrev);
    this.carouselNext = this.carousel.querySelector(selectors.carouselNext);

    this.container.addEventListener('theme:tab:change', () => {
      this.carouselInstance.resize();
      this.carouselPrev = this.carousel.querySelector(selectors.carouselPrev);
      this.carouselNext = this.carousel.querySelector(selectors.carouselNext);
    });

    this.carouselInstance.on('dragStart', () => {
      this.carouselInstance.slider.style.pointerEvents = 'none';
      if (!resolution.isMobile) this.containDrag();
    });
    this.carouselInstance.on('dragEnd', () => {
      this.carouselInstance.slider.style.pointerEvents = 'auto';
      if (!resolution.isMobile) this.containDrag();
    });
    this.carouselInstance.on('change', (index) => this.lockArrows(index));

    setTimeout(() => {
      this.visibleSlides();
    }, 100);

    if (Shopify.designMode) {
      setTimeout(() => {
        if (this.carouselInstance.options.watchCSS && !this.carousel.classList.contains(classes.flickityEnabled)) {
          this.carouselInstance.destroy();
          this.carouselInstance = new Flickity(this.carousel, this.options);
          this.carouselInstance.resize();
          this.carouselPrev = this.carousel.querySelector(selectors.carouselPrev);
          this.carouselNext = this.carousel.querySelector(selectors.carouselNext);
        } else {
          this.carouselInstance.resize();
        }
      }, 10);
    }

    this.carousel.classList.toggle(classes.flickityStatic, this.smallItems === this.carousel.querySelectorAll(selectors.carouselSlide).length);

    makeGridSwatches(this.container);
    new Siblings(this.container);
  }

  calculatedArrowsTopPosition() {
    const carouselFirstSlidePhoto = this.container.querySelector(selectors.carouselFirstSlidePhoto);
    const pgiFirstSlidePhoto = this.container.querySelector(selectors.pgiFirstSlidePhoto);

    if (pgiFirstSlidePhoto) {
      const buttonOffset = pgiFirstSlidePhoto.offsetHeight / 2;
      this.carousel.style.setProperty('--buttons-top', `${buttonOffset}px`);
    } else if (carouselFirstSlidePhoto) {
      const buttonOffset = carouselFirstSlidePhoto.offsetHeight / 2;
      this.carousel.style.setProperty('--buttons-top', `${buttonOffset}px`);
    }
  }

  toggleWrapperModifier() {
    if (!this.wrapper) {
      return;
    }
    const scrollbarWidth = Number(getComputedStyle(document.documentElement).getPropertyValue('--scrollbar-width').replace('px', ''));
    const wrapperWidth = this.wrapper.clientWidth;
    this.wrapperWidthWithGutter = wrapperWidth + offsets.additionalOffsetWrapper + scrollbarWidth;

    if (window.innerWidth >= this.wrapperWidthWithGutter) {
      // the screen is wide, have the arrows beside the carousel
      this.wrapper.classList.remove(classes.arrowsForceTop);
      this.section.classList.add(classes.arrowsOnSide);
    }
    if (window.innerWidth < this.wrapperWidthWithGutter) {
      // the screen is too narrow for arrows beside the carousel
      // add the wrapper--full class to trick the layout
      this.wrapper.classList.add(classes.arrowsForceTop);
      this.section.classList.remove(classes.arrowsOnSide);
    }
  }

  progressBarCalculate() {
    if (this.carouselInstance !== null && this.carouselTrack) {
      this.carouselInstance.on('scroll', (progress) => {
        progress = Math.max(0, Math.min(1, progress)) * 100 + '%';
        this.carouselTrack.style.width = progress;
      });
    }
  }

  getGridLayout() {
    this.largeItems = Number(getComputedStyle(this.carousel).getPropertyValue('--grid-large-items')) || 3;
    this.mediumItems = Number(getComputedStyle(this.carousel).getPropertyValue('--grid-medium-items')) || this.largeItems;
    this.smallItems = Number(getComputedStyle(this.carousel).getPropertyValue('--grid-small-items')) || this.mediumItems || this.largeItems;
  }

  visibleSlides() {
    if (!this.carousel) {
      return;
    }

    this.getGridLayout();

    const carouselWidth = this.carousel.clientWidth || this.carouselInstance.size.width;
    const slideWidth =
      this.carouselInstance !== null && this.carouselInstance.selectedElement ? this.carouselInstance.selectedElement.clientWidth : this.carousel.querySelector(selectors.carouselSlide).clientWidth;
    const countSlides = this.carouselInstance !== null && this.carouselInstance.slides ? this.carouselInstance.slides.length : this.carousel.querySelectorAll(selectors.carouselSlide).length;
    const numberOfVisibleSlides = Math.floor(carouselWidth / slideWidth);

    this.section.classList.remove(classes.hiddenArrows);

    if (this.carouselPrev && this.carouselNext) {
      this.carouselPrev.classList.remove(classes.hide);
      this.carouselNext.classList.remove(classes.hide);
    }

    // Desktop
    if (window.innerWidth > window.theme.sizes.large && !this.options.groupCells) {
      if (numberOfVisibleSlides <= this.largeItems && countSlides <= this.largeItems && this.carouselPrev && this.carouselNext) {
        this.hideArrows();
      }
    }

    // Tablet
    if (window.innerWidth >= window.theme.sizes.medium && window.innerWidth <= window.theme.sizes.large && !this.options.groupCells) {
      if (numberOfVisibleSlides <= this.mediumItems && countSlides <= this.mediumItems && this.carouselPrev && this.carouselNext) {
        this.hideArrows();
      }
    }

    // Mobile
    if (window.innerWidth < window.theme.sizes.medium && !this.options.groupCells) {
      if (numberOfVisibleSlides <= this.smallItems && countSlides <= this.smallItems && this.carouselPrev && this.carouselNext) {
        this.hideArrows();
      }
    }
  }

  trackVisibleSlides() {
    const isSmallDown = window.matchMedia(`(max-width: ${window.theme.sizes.medium - 1}px)`);
    const isTablet = window.matchMedia(`(min-width: ${window.theme.sizes.medium}px) and (max-width: ${window.theme.sizes.large - 1}px)`);
    const isDesktop = window.matchMedia(`(min-width: ${window.theme.sizes.large}px)`);
    isSmallDown.addEventListener('change', (event) => {
      event.matches ? (this.slidesVisible = this.smallItems) : true;
    });
    isSmallDown.matches ? (this.slidesVisible = this.smallItems) : true;

    isTablet.addEventListener('change', (event) => {
      event.matches ? (this.slidesVisible = this.mediumItems) : true;
    });
    isTablet.matches ? (this.slidesVisible = this.mediumItems) : true;

    isDesktop.addEventListener('change', (event) => {
      event.matches ? (this.slidesVisible = this.largeItems) : true;
    });
    isDesktop.matches ? (this.slidesVisible = this.largeItems) : true;
  }

  containDrag() {
    // Dragging agressively in flickity will select the last cell in the list
    // instead of the first cell of the last slide (slide is a set of cells).
    // We detect drag events, and move the selection back to the first cell
    // of the last slide -- the lastSelectableCell.
    const lastSelectableCell = this.slidesTotal - this.slidesVisible;
    if (this.carouselInstance.selectedIndex >= lastSelectableCell) {
      this.carouselInstance.select(lastSelectableCell);
      this.lockArrows(this.carouselInstance.selectedIndex);
    }
  }

  lockArrows(index) {
    if (this.options.wrapAround || this.options.groupCells) {
      return;
    }
    const nextIndex = parseInt(index);

    const lastSelectableCell = this.slidesTotal - this.slidesVisible;
    this.carouselNext.disabled = nextIndex >= lastSelectableCell;
  }

  showArrows() {
    this.carouselPrev.classList.remove(classes.hide);
    this.carouselNext.classList.remove(classes.hide);
    this.section.classList.remove(classes.hiddenArrows);
  }

  hideArrows() {
    this.carouselPrev.classList.add(classes.hide);
    this.carouselNext.classList.add(classes.hide);
    this.section.classList.add(classes.hiddenArrows);
  }

  toggleArrows() {
    if (this.carouselPrev && this.carouselNext) {
      if (this.carouselPrev.disabled && this.carouselNext.disabled) {
        this.hideArrows();
      } else {
        this.showArrows();
      }
    }
  }
}

if (!customElements.get('flickity-carousel')) {
  customElements.define('flickity-carousel', Carousel);
}
